/* eslint-disable import/prefer-default-export */
/**
 * Singleton Carveco/Vectric parser worker. Same request/response shape as
 * useGcodeParser, but transfers an ArrayBuffer (these files are binary and
 * tens of megabytes, so the buffer is transferred, not copied).
 */
import { CamExtractedData } from './camTypes';

type Pending = {
  resolve: (data: CamExtractedData | null) => void;
  reject: (err: Error) => void;
};

let worker: Worker | null = null;
const pending = new Map<string, Pending>();

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./camParser.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (e: MessageEvent) => {
      const { fileId, result, error } = e.data;
      const p = pending.get(fileId);
      if (!p) return;
      pending.delete(fileId);
      if (error) p.reject(new Error(error));
      else p.resolve(result ?? null);
    };
    worker.onerror = (err) => {
      pending.forEach((p) =>
        p.reject(new Error(`CAM worker error: ${err.message}`))
      );
      pending.clear();
      // A worker that has errored at this level (not just a rejected message)
      // is presumed dead — nothing further will ever post back to it. Null it
      // so the next parse call builds a fresh one instead of posting into the
      // void forever (see useGcodeParser's terminateGcodeParser for the same
      // idea on an explicit terminate).
      worker = null;
    };
  }
  return worker;
}

/**
 * Parse a CAM file in the worker.
 *
 * The buffer is TRANSFERRED, not copied: on return the caller's `buffer` is
 * detached and reads as zero length. Reading a detached buffer does not throw,
 * it silently yields nothing — so never reuse the buffer after this call, and
 * never call this twice with the same one. Keep the originating `File`/`Blob`
 * around instead; it can be re-read with another `arrayBuffer()` call.
 */
export async function parseCamInWorker(
  buffer: ArrayBuffer
): Promise<CamExtractedData | null> {
  const fileId = `cam-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const w = getWorker();
  return new Promise<CamExtractedData | null>((resolve, reject) => {
    pending.set(fileId, { resolve, reject });
    try {
      w.postMessage({ fileId, buffer }, [buffer]);
    } catch (err) {
      pending.delete(fileId);
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}
